import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDirtyTracker } from '../../../../context/UpdateContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormControlLabel, Modal } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ProductChangeHistoryModal from '../../common/ProductChangeHistoryModal';
import { GridCloseIcon } from '@mui/x-data-grid';
import ProductPickerModal from '../../common/ProductPickerModal';
import ClassSelectModal from './ClassSelectModal';

import { createTicket, getTicketById, updateTicket, deleteTicketById, changeStatus, getAvailableClasses, assignTicketToClasses } from '../../../../api/ticket';
import { toggleTicketExposure } from '../../../../api/exposure';
import { FAVORITE_TARGET } from '../../../../api/favorite';
import FavoriteStar from '../../../common/FavoriteStar';
import useFavorite from '../../../../utils/useFavorite';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import { getPointTrainers } from '../../../../api/staff';
import { getBranchProductsByIdNoQuery, getDirectBranches } from '../../../../api/point';
import { errorMessages, validateDateRange, sellStatus } from '../../../../utils/methods';
import { appendField, appendFields } from '../../../../utils/voucherFormData';
import IOSSwitch from '../../IosSwitch';
import useUserAuth from '../../../../utils/useUserAuth';

import {
  PageContainer,
  Header,
  HeaderTitle,
  HeaderLink,
  BackButton,
  HeaderBackLink,
  EditModeHeader,
  StatusToggleWrapper,
  StatusToggleLabel,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  TicketInfoWrapper,
  TicketInfoHeader,
  TicketInfoTitle,
  StatusBadge,
  CopyButton,
  LoadProductButton,
  HeaderActionGroup,
  ModalIconBadge,
  CopyModeBanner,
  CopyModeBannerTitle,
  CopyModeBannerText,
  WishlistWrapper,
  WishlistIcon,
  WishlistText,
  Divider,
  ModalContainer,
  ModalCloseIcon,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalDescText,
  ModalDescTextSub,
  ModalButtonWrapper,
  ModalCancelButton,
  ModalConfirmButton,
  StepIndicatorContainer,
  StepTabsWrapper,
  StepTab,
  StepTabShapeFirst,
  StepTabShapeSecond,
  StepTabContent,
  StepTabNumber,
  StepTabLabel,
  BackToListLink,
  BackToListIcon,
  BackToListText,
} from './TicketEditStyled';
import TicketEditStep01 from './TicketEditStep01';
import TicketEditStep02 from './TicketEditStep02';
import { centersDetail } from '../../../../api/store-management';

// 요일 목록
const DAYS = [
  { label: '월', value: 'MON' },
  { label: '화', value: 'TUE' },
  { label: '수', value: 'WED' },
  { label: '목', value: 'THU' },
  { label: '금', value: 'FRI' },
  { label: '토', value: 'SAT' },
  { label: '일', value: 'SUN' },
];

// 시간 옵션 (00-23)
const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

// 분 옵션
const MINUTES = ['00', '30'];

// 수업 유형
const CLASS_TYPES = {
  GROUP: 'GROUP_LESSON',
  PRIVATE: 'PRIVATE_LESSON',
  DUET: 'DUET_LESSON',
  OT: 'OT',
};

function TicketEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // 복사: /ticket/create?copyFrom={id} — 편집 로더를 그대로 재사용한다
  const copyFromId = searchParams.get('copyFrom');
  const isEditMode = !!id;
  const isCopyMode = !isEditMode && !!copyFromId;
  const [legacyBranchNames, setLegacyBranchNames] = useState({});

  const methods = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const canCreate = useUserAuth('TICKET_COURSE_CREATE');
  const canEdit = useUserAuth('TICKET_COURSE_EDIT');
  const canSave = isEditMode ? canEdit : canCreate;
  const aaa = useSelector(state => state.user);

  const { handleSubmit, control, setValue, watch, reset, getValues } = methods;
  useDirtyTracker(methods.formState.isDirty);

  // 기본 상태
  const [loader, setLoader] = useState(false);
  const [step, setStep] = useState(1);
  const [trainers, setTrainers] = useState([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  // 지점 공용 즐겨찾기(상세 헤더 별표)
  const [favorite, setFavorite] = useFavorite(FAVORITE_TARGET.TICKET, id, isEditMode);
  const [classSelectModalOpen, setClassSelectModalOpen] = useState(false);
  const [newTicketId, setNewTicketId] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [copySourceName, setCopySourceName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Step1 관련 상태
  const [ticketData, setTicketData] = useState({
    voucherList: [],
    trainerIdsList: [],
    curList: [{ title: '', outline: '', curImg: {} }],
    finalPrice: '',
    wishListAddedCount: 0,
  });
  const [additionalProductSettings, setAdditionalProductSettings] = useState([]);
  const [usagePeriodType, setUsagePeriodType] = useState('MONTH');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSettings, setTimeSettings] = useState({
    startHour: '00',
    startMinute: '00',
    endHour: '00',
    endMinute: '00',
  });
  const [isTaxFree, setIsTaxFree] = useState(false);
  const [exposureYn, setExposureYn] = useState('Y');
  // 입장 가능 출입문 — 선택된 출입문(BranchDoor) id 목록. 미선택(빈 배열)이면 accessGroupIds 를 전송하지 않는다.
  const [selectedDoorIds, setSelectedDoorIds] = useState([]);
  // 출입문 체계 전환 지점 여부 — 전환 지점은 저장 시 항상 accessGroupIds 를 전송한다.
  const [doorBranchConverted, setDoorBranchConverted] = useState(false);

  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);

  const handleExposureToggle = async () => {
    const newValue = exposureYn === 'Y' ? 'N' : 'Y';
    const saleStatus = watch('saleStatus');
    if (newValue === 'Y' && (saleStatus === 'EXPIRED' || saleStatus === 'SALE_END')) {
      setExposureAlertOpen(true);
      return;
    }
    setExposureYn(newValue);
    try {
      await toggleTicketExposure(user.token, id, newValue);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      setExposureYn(exposureYn);
      toast.error(errorMessages(error));
    }
  };

  const [discountType, setDiscountType] = useState('원');
  const [hasDiscountEndDate, setHasDiscountEndDate] = useState(false);

  // Step2 관련 상태
  const [aiToggle, setAiToggle] = useState(false);
  const [classDetail, setClassDetail] = useState('');
  const [voucherImages, setVoucherImages] = useState([]);
  const [curList, setCurList] = useState([{ title: '', outline: '', images: [] }]);

  const { data: centrerInfo } = useQuery({
    queryKey: ['centerInfo', user?.branch?.centerId],
    queryFn: () => centersDetail(user?.token, user?.branch?.centerId),
    enabled: !!user?.branch?.centerId,
  });

  // 같은 본사 산하 직영점 목록 (#73 — 지점 계정도 본사 직영점 전체를 받는 전용 API)
  const { data: directBranches = [] } = useQuery({
    queryKey: ['directBranches', user?.branch?.branchId],
    queryFn: () => getDirectBranches(user.token, user.branch?.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 제품 목록 조회
  const { data } = useQuery({
    queryKey: ['productList', user?.branch?.branchId],
    queryFn: () => getBranchProductsByIdNoQuery(user?.token, user?.branch?.branchId),
    enabled: !!user?.branch?.branchId,
  });

  // 강사 목록 조회
  useEffect(() => {
    if (!user?.branch?.branchId) return;
    getPointTrainers(user.branch.branchId, user.token)
      .then(data => setTrainers(data))
      .catch(err => console.log(err));
  }, [user?.branch?.branchId, user?.token]);

  // 판매종료일 기본값 설정
  useEffect(() => {
    if (!watch('hasSaleEndDate') && !isEditMode) {
      setValue('hasSaleEndDate', true);
    }
    // 중지권 기본값: 무제한 (생성 모드)
    if (!isEditMode && !watch('stopType')) {
      setValue('stopType', 'UNLIMITED');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 편집(:id) / 복사(?copyFrom) 모두 같은 로더를 쓴다
  useEffect(() => {
    const sourceId = id ?? copyFromId;
    if (sourceId) {
      loadTicketData(sourceId, isCopyMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, copyFromId]);

  // 생성 모드 기본 판매·이용지점 = 로그인 지점 (#73)
  useEffect(() => {
    if (!isEditMode && user?.branch?.branchId) {
      if (!getValues('saleBranchIds')?.length) setValue('saleBranchIds', [user.branch.branchId]);
      if (getValues('useSaleBranchYn') !== 'Y' && !getValues('useBranchIds')?.length) setValue('useBranchIds', [user.branch.branchId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, user?.branch?.branchId]);

  const loadTicketData = async (sourceId, forCopy = false) => {
    try {
      const data = await getTicketById(sourceId, user.token);
      setTicketData({
        ...data,
        trainerIdsList:
          data.trainers?.map(item => ({
            id: item.trainerId,
            name: item.trainerName,
          })) || [],
        voucherList:
          data.images?.map(item => ({
            voucherUrl: item,
          })) || [],
        curList:
          data.curriculums?.map(item => ({
            curriculumId: item.curriculumId,
            curImg: item.image,
            outline: item.summary,
            title: item.title,
            fileName: item.image,
          })) || [],
        wishListAddedCount: data.wishListAddedCount || 0,
      });

      reset({
        ...data,
        sportsType: data.sportTypes ?? data.sportType,
        courseClassType: data.classType,
        // #73 판매·이용지점 프리필 — 비공유 상품은 소유 지점 1개
        saleBranchIds: data.saleBranchIds?.length
          ? data.saleBranchIds
          : (data.participatingBranchIds?.length ? data.participatingBranchIds : [data.branchId].filter(Boolean)),
        // #98 소속지점 모드면 이용지점 목록은 비어 있는 게 정상
        useSaleBranchYn: data.useSaleBranchYn === 'Y' ? 'Y' : 'N',
        useBranchIds: data.useSaleBranchYn === 'Y'
          ? []
          : data.useBranchIds?.length
            ? data.useBranchIds
            : (data.participatingBranchIds?.length ? data.participatingBranchIds : [data.branchId].filter(Boolean)),
      });
      // #73 판매/이용 옵션(직영점 목록)에 없는 참여지점(과거 가맹점 데이터 등) 이름 폴백용
      setLegacyBranchNames(
        (data.participatingBranchIds || []).reduce((acc, bid, idx) => {
          acc[bid] = data.participatingBranchNames?.[idx] ?? `지점 #${bid}`;
          return acc;
        }, {}),
      );

      if (data.exposureYn) setExposureYn(data.exposureYn);
      setSelectedDoorIds((data.accessGroups ?? []).map(g => g.id));

      // Step2 이미지 데이터 설정
      if (data.images && data.images.length > 0) {
        setVoucherImages(data.images.map(url => ({ url, preview: url })));
      }

      // 커리큘럼 데이터 설정
      if (data.curriculums && data.curriculums.length > 0) {
        setCurList(
          data.curriculums.map(item => ({
            title: item.title || '',
            outline: item.summary || '',
            images: item.images?.length > 0 ? item.images.map(url => ({ url, preview: url })) : [],
          })),
        );
      }

      // 수강권 상세 설정
      if (data.classDetail) {
        setClassDetail(data.classDetail);
      }

      // 추가 상품 설정
      if (data.products) {
        const products = data.products.map((el, index) => {
          setValue(`settings-name-${index}`, el.name);
          setValue(`settings-price-${index}`, el.price);
          return {
            name: el.name,
            price: el.price,
            use: el.status === 'USED',
          };
        });
        setAdditionalProductSettings(products);
      }

      setValue('kiosk', data.kioskProduct);

      // 이용 제한 설정
      if (data.dailyUsageLimit) {
        setValue('dailyUsageLimit', data.dailyUsageLimit);
        setValue('dailyUsageLimits', false);
      } else {
        setValue('dailyUsageLimits', true);
      }

      if (data.weeklyUsageLimit) {
        setValue('weeklyUsageLimit', data.weeklyUsageLimit);
        setValue('weeklyUsageLimits', false);
      } else {
        setValue('weeklyUsageLimits', true);
      }

      // 중지권 매핑: null=무제한, 0=중지불가, 양수=직접입력
      if (data.stopCount !== null && data.stopCount !== undefined && data.stopCount !== '') {
        setValue('stopCount', data.stopCount);
        setValue('stopPeriod', data.stopPeriod);
        setValue('stopType', Number(data.stopCount) > 0 ? 'CUSTOM' : 'DISABLED');
      } else {
        setValue('stopType', 'UNLIMITED');
      }

      if (data.cancelCount != null) {
        setValue('cancelCount', data.cancelCount);
        setValue('cancelCounts', false);
      } else {
        setValue('cancelCounts', true);
      }

      if (data.reservationCount) {
        setValue('reservationCount', data.reservationCount);
        setValue('reservationCounts', false);
      } else {
        setValue('reservationCounts', true);
      }

      // 자동 차감: count 존재 여부가 on/off. 기준일은 시작일(START_DATE) 또는 매월 지정 일자
      if (data.autoDeductionCount != null) {
        setValue('autoDeductionEnabled', true);
        setValue('autoDeductionCount', data.autoDeductionCount);
        if (data.autoDeductionBaseType === 'START_DATE') {
          setValue('autoDeductionDayMode', 'START');
        } else {
          setValue('autoDeductionDayMode', 'DAY');
          setValue('autoDeductionBaseDay', data.autoDeductionBaseDay ?? 1);
        }
      } else {
        setValue('autoDeductionEnabled', false);
      }

      // 할인 타입 설정 (RATE → %, AMOUNT → 원)
      if (data.discountType) {
        setDiscountType(data.discountType === 'AMOUNT' ? '원' : '%');
      }

      setUsagePeriodType(data.usagePeriodType === 'MONTH' ? 'MONTH' : 'DAY');
      data.hasSaleEndDate ? setValue('hasSaleEndDate', !data.hasSaleEndDate) : setValue('hasSaleEndDate', true);

      // 요일 설정 로드 (allowedDays: "MON,TUE,WED" 형식) - 중복 제거
      if (data.allowedDays) {
        const days = data.allowedDays.split(',').filter(d => d.trim());
        const uniqueDays = [...new Set(days)];
        setSelectedDays(uniqueDays);
      }

      // 시간 설정 로드 (startTime: "09:00:00", endTime: "18:00:00" 형식)
      if (data.startTime) {
        const [startHour, startMinute] = data.startTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          startHour: startHour || '00',
          startMinute: startMinute || '00',
        }));
      }
      if (data.endTime) {
        const [endHour, endMinute] = data.endTime.split(':');
        setTimeSettings(prev => ({
          ...prev,
          endHour: endHour || '00',
          endMinute: endMinute || '00',
        }));
      }

      // 복사본은 이름만 비우고(원본명은 placeholder 로 안내) 미노출 상태로 시작한다
      if (forCopy) {
        setCopySourceName(data.ticketName ?? '');
        setValue('ticketName', '');
        setExposureYn('N');
      }
    } catch (error) {
      console.log('error', error);
      toast.error(errorMessages(error));
    }
  };

  // 가격 계산 로직
  useEffect(() => {
    if (!lastChangedField) return;

    const regularPrice = watch('regularPrice');
    const discount = watch('discountRate');
    const finalPrice = watch('finalPrice');

    const regularPriceNum = Number(regularPrice?.toString().replace(/,/g, '') || 0);
    const discountNum = Number(discount?.toString().replace(/,/g, '') || 0);
    const finalPriceNum = Number(finalPrice?.toString().replace(/,/g, '') || 0);

    if (lastChangedField === 'regularPrice' && regularPriceNum > 0) {
      setValue('finalPrice', regularPriceNum);
      setValue('discountRate', 0);
    } else if (lastChangedField === 'discountRate' && regularPriceNum > 0) {
      if (discountNum < 0) {
        toast.error(discountType === '원' ? '할인금액은 음수가 될 수 없습니다' : '할인율은 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (discountType === '%') {
        if (discountNum > 100) {
          toast.error('할인율은 100%를 초과할 수 없습니다');
          setLastChangedField(null);
          return;
        }
        const calculatedFinalPrice = Math.round(regularPriceNum - (regularPriceNum * discountNum) / 100);
        setValue('finalPrice', calculatedFinalPrice);
      } else {
        if (discountNum > regularPriceNum) {
          toast.error('할인금액이 정상가보다 클 수 없습니다');
          setLastChangedField(null);
          return;
        }
        setValue('finalPrice', regularPriceNum - discountNum);
      }
    } else if (lastChangedField === 'finalPrice' && regularPriceNum > 0) {
      if (finalPriceNum > regularPriceNum) {
        toast.error('최종가는 정상가보다 클 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (finalPriceNum < 0) {
        toast.error('최종가는 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }
      if (discountType === '%') {
        let calculatedDiscountRate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
        if (finalPriceNum > 0 && calculatedDiscountRate >= 100) {
          calculatedDiscountRate = 99;
        }
        setValue('discountRate', calculatedDiscountRate);
      } else {
        setValue('discountRate', regularPriceNum - finalPriceNum);
      }
    }
    setLastChangedField(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChangedField]);

  // 단위 전환 시 기존 값 변환 (% ↔ 원)
  useEffect(() => {
    const regularPriceNum = Number(getValues('regularPrice')?.toString().replace(/,/g, '') || 0);
    const finalPriceNum = Number(getValues('finalPrice')?.toString().replace(/,/g, '') || 0);
    if (regularPriceNum <= 0) return;

    if (discountType === '원') {
      setValue('discountRate', Math.max(0, regularPriceNum - finalPriceNum));
    } else {
      const rate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
      setValue('discountRate', Math.max(0, Math.min(rate, 99)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountType]);

  // 핸들러 함수들
  const handleTrainerOnChange = selected => {
    ticketData.trainerIdsList = selected;
  };

  const handleDayToggle = dayValue => {
    setSelectedDays(prev => (prev.includes(dayValue) ? prev.filter(d => d !== dayValue) : [...prev, dayValue]));
  };

  const handleTimeChange = (field, value) => {
    setTimeSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAddProduct = product => {
    const isDuplicate = additionalProductSettings.some(item => item.name === product.productName);
    if (isDuplicate) {
      toast.error(`${product.productName}은(는) 이미 추가된 상품입니다.`);
      return;
    }
    const currentIndex = additionalProductSettings.length;
    setAdditionalProductSettings(prev => [...prev, { name: product.productName, price: product.price, use: false }]);
    setValue(`settings-name-${currentIndex}`, product.productName);
    setValue(`settings-price-${currentIndex}`, product.price);
  };

  const handleRemoveProduct = index => {
    setAdditionalProductSettings(prev => prev.filter((_, i) => i !== index));
  };

  const handleRegularPriceChange = () => setLastChangedField('regularPrice');
  const handleDiscountRateChange = () => setLastChangedField('discountRate');
  const handleFinalPriceChange = () => setLastChangedField('finalPrice');

  // Step2 이미지 핸들러
  const handleVoucherImageUpload = e => {
    const files = Array.from(e.target.files);
    if (voucherImages.length + files.length > 7) {
      toast.error('이미지는 최대 7장까지 업로드 가능합니다.');
      return;
    }
    const newImages = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setVoucherImages(prev => [...prev, ...newImages]);
  };

  const handleRemoveVoucherImage = index => {
    setVoucherImages(prev => prev.filter((_, i) => i !== index));
  };

  // 커리큘럼 핸들러
  const handleAddCurriculum = () => {
    if (curList.length >= 5) {
      toast.error('커리큘럼은 최대 5개까지 등록 가능합니다.');
      return;
    }
    setCurList(prev => [...prev, { title: '', outline: '', images: [] }]);
  };

  const handleRemoveCurriculum = index => {
    setCurList(prev => prev.filter((_, i) => i !== index));
  };

  const handleCurriculumChange = (index, field, value) => {
    setCurList(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleCurriculumImageUpload = (index, e) => {
    const files = Array.from(e.target.files);
    const currentImages = curList[index].images || [];
    if (currentImages.length + files.length > 3) {
      toast.error('이미지는 최대 3장까지 업로드 가능합니다.');
      return;
    }
    const newImages = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
    handleCurriculumChange(index, 'images', [...currentImages, ...newImages]);
  };

  const handleRemoveCurriculumImage = (curIndex, imgIndex) => {
    const currentImages = curList[curIndex].images || [];
    handleCurriculumChange(
      curIndex,
      'images',
      currentImages.filter((_, i) => i !== imgIndex),
    );
  };

  // Step 이동
  const handleNextStep = () => {
    handleSubmit(values => {
      if (!values.ticketName) {
        toast.error('수강권명을 입력해주세요.');
        return;
      }
      if (!values.usageCount) {
        toast.error('이용 횟수를 입력해주세요.');
        return;
      }
      if (!values.classDefaultMinute) {
        toast.error('수업시간을 입력해주세요.');
        return;
      }
      if (!values.regularPrice) {
        toast.error('판매가를 입력해주세요.');
        return;
      }
      if (values.autoDeductionEnabled) {
        const autoDeductionCount = Number(values.autoDeductionCount);
        if (!autoDeductionCount || autoDeductionCount < 1) {
          toast.error('자동 차감 횟수를 1 이상 입력해주세요.');
          return;
        }
        if (values.autoDeductionDayMode !== 'START') {
          const autoDeductionBaseDay = Number(values.autoDeductionBaseDay);
          if (!autoDeductionBaseDay || autoDeductionBaseDay < 1 || autoDeductionBaseDay > 31) {
            toast.error('자동 차감 일자는 1~31 사이로 입력해주세요.');
            return;
          }
        }
      }
      setStep(2);
    })();
  };

  const handlePrevStep = () => setStep(1);
  const handleCancel = () => navigate(-1);

  // Edit 모드 전용 핸들러들
  const handleChangeStatus = async e => {
    const status = e.target.checked ? 'APPROVED' : 'EXPIRED';
    try {
      await changeStatus(status, id, user.token);
      toast.success(t('Ticket status Changed'));
      setValue('saleStatus', status);
      if (status === 'EXPIRED') setExposureYn('N');
    } catch (error) {
      console.log('error', error);
      toast.error(errorMessages(error));
    }
  };

  const handleDeleteTicket = () => {
    deleteTicketById(id, user.token)
      .then(() => {
        toast.success(t('Ticket Deleted'));
        navigate('/ticket');
      })
      .catch(err => {
        console.log(err);
        toast.error(errorMessages(err));
      });
  };

  const handleCopyTicket = () => {
    navigate(`/ticket/create?copyFrom=${id}`);
  };

  // Submit
  const submit = async values => {
    setLoader(true);

    const {
      branchName,
      voucherList: originalVoucherList,
      curList: originalCurList,
      trainerIdsList,
      ...filterTicketData
    } = ticketData;
    const { title, outline, curImg, fileName, ...filterValues } = values;

    const hasSaleEndDate = values.hasSaleEndDate ?? false;
    if (!hasSaleEndDate && !validateDateRange(values.saleStartDate, values.saleEndDate)) {
      toast.error('기간을 다시 확인해주세요');
      setLoader(false);
      return;
    }

    const saleEndDateFormatted =
      hasSaleEndDate || !filterValues.saleEndDate || !dayjs(filterValues.saleEndDate).isValid()
        ? ''
        : dayjs(filterValues.saleEndDate).format('YYYY-MM-DD');

    const discountStartDateFormatted =
      filterValues.discountStartDate && dayjs(filterValues.discountStartDate).isValid()
        ? dayjs(filterValues.discountStartDate).format('YYYY-MM-DD')
        : '';

    const discountEndDateFormatted =
      hasDiscountEndDate || !filterValues.discountEndDate || !dayjs(filterValues.discountEndDate).isValid()
        ? ''
        : dayjs(filterValues.discountEndDate).format('YYYY-MM-DD');

    const filteredData = {
      ...filterTicketData,
      ...filterValues,
      saleStartDate: dayjs(filterValues.saleStartDate).format('YYYY-MM-DD'),
      saleEndDate: saleEndDateFormatted,
      discountStartDate: discountStartDateFormatted,
      discountEndDate: discountEndDateFormatted,
      branchId: user?.branch?.branchId ?? '',
      branchName: user?.branch?.branchName ?? '',
      hasSaleEndDate,
      hasDiscountEndDate,
      classDetail: classDetail ?? '',
    };

    const formData = new FormData();

    // allow-list: 백엔드 DTO 가 받는 스칼라 필드만 명시 전송한다.
    // (sportType/가격/기간/요일/시간/이용제한 등 나머지는 아래에서 가공해 개별 append)
    // #73 판매지점·이용지점 — 합집합 2개 이상이면 공유 상품으로 저장된다
    const saleToSend = (getValues('saleBranchIds') || []).filter(Boolean);
    const useToSend = (getValues('useBranchIds') || []).filter(Boolean);
    // #98 소속지점 모드 — 이용지점 목록 대신 플래그만 보낸다(백엔드가 이용 행을 비운다)
    const useSaleBranch = getValues('useSaleBranchYn') === 'Y';
    formData.append('useSaleBranchYn', useSaleBranch ? 'Y' : 'N');
    (saleToSend.length ? saleToSend : [user?.branch?.branchId]).forEach(bid => {
      formData.append('saleBranchIds', bid);
    });
    if (!useSaleBranch) {
      (useToSend.length ? useToSend : [user?.branch?.branchId]).forEach(bid => {
        formData.append('useBranchIds', bid);
      });
    }

    appendFields(formData, filteredData, [
      'branchId',
      'ticketName',
      'saleStartDate',
      'saleEndDate',
      'hasSaleEndDate',
      'discountStartDate',
      'discountEndDate',
      'hasDiscountEndDate',
    ]);

    const courseClassTypeValue =
      values.courseClassType || filterTicketData.classType || filterTicketData.courseClassType;
    if (courseClassTypeValue) {
      formData.append('courseClassType', courseClassTypeValue);
    }

    // 강사 선택 UI가 주석 처리되어 있는 동안에는 검증 + 전송 모두 비활성화.
    // TicketEditStep01의 강사선택 FieldGroup을 재활성화할 때 같이 풀어주면 된다.
    // if (
    //   (courseClassTypeValue === CLASS_TYPES.PRIVATE || courseClassTypeValue === CLASS_TYPES.DUET) &&
    //   trainerIdsList.length < 1
    // ) {
    //   toast.error('참여 강사를 1명 이상 등록 해주세요');
    //   setLoader(false);
    //   return;
    // }
    //
    // trainerIdsList.forEach((trainer, index) => {
    //   formData.append(`trainers[${index}]`, trainer.id);
    // });

    const currentSportsType = getValues('sportsType');
    const rawSportTypes =
      currentSportsType || filterValues.sportsType || filterTicketData.sportsType || filterTicketData.sportType;
    const sportTypesArr = Array.isArray(rawSportTypes)
      ? rawSportTypes
      : rawSportTypes
      ? String(rawSportTypes).split(',').filter(Boolean)
      : [];
    if (sportTypesArr.length > 0) {
      formData.append('sportType', sportTypesArr[0]); // 대표값(단일)
      appendField(formData, 'sportTypes', sportTypesArr); // 복수 종목(키 반복)
    }

    additionalProductSettings.forEach((products, index) => {
      formData.append(`products[${index}].name`, values[`settings-name-${index}`] || '');
      formData.append(`products[${index}].price`, Number(values[`settings-price-${index}`]) || 0);
      formData.append(`products[${index}].status`, products.use ? 'USED' : 'UNUSED');
    });

    formData.append('usagePeriodType', usagePeriodType);
    if (values.usagePeriod) formData.append('usagePeriod', values.usagePeriod);
    if (values.usageCount) formData.append('usageCount', values.usageCount);

    // 수강권은 구독 상품이 아님
    formData.append('subscriptionYn', false);
    formData.append('exposureYn', exposureYn);
    if (values.classDefaultMinute) formData.append('classDefaultMinute', values.classDefaultMinute);

    const appendFormData = (key, conditionValue, dataValue) => {
      if (conditionValue || dataValue === undefined || dataValue === null || dataValue === '' || dataValue === true) {
        formData.append(key, '');
      } else {
        formData.append(key, dataValue);
      }
    };

    const parsePrice = value => {
      if (value === null || value === undefined) return 0;
      return Number(value.toString().replace(/,/g, ''));
    };

    formData.append('discountRate', parsePrice(values.discountRate));
    formData.append('discountType', discountType === '%' ? 'RATE' : 'AMOUNT');
    formData.append('finalPrice', parsePrice(values.finalPrice));
    formData.append('regularPrice', parsePrice(values.regularPrice));

    appendFormData('dailyUsageLimit', getValues('dailyUsageLimits'), values.dailyUsageLimit);
    appendFormData('weeklyUsageLimit', getValues('weeklyUsageLimits'), values.weeklyUsageLimit);
    // 중지권: 무제한=빈값(null), 중지불가=0, 직접입력=양수
    const stopType = getValues('stopType');
    if (stopType === 'UNLIMITED') {
      formData.append('stopCount', '');
      formData.append('stopPeriod', '');
    } else if (stopType === 'DISABLED') {
      formData.append('stopCount', 0);
      formData.append('stopPeriod', 0);
    } else {
      formData.append('stopCount', values.stopCount);
      formData.append('stopPeriod', values.stopPeriod);
    }
    appendFormData('cancelCount', getValues('cancelCounts'), values.cancelCount);
    appendFormData('reservationCount', getValues('reservationCounts'), values.reservationCount);

    // 자동 차감: 사용 시에만 전송. 미전송이면 백엔드가 null 저장(해제)
    if (getValues('autoDeductionEnabled') && values.autoDeductionCount) {
      formData.append('autoDeductionCount', values.autoDeductionCount);
      formData.append('autoDeductionCycleType', 'MONTH');
      if (values.autoDeductionDayMode === 'START') {
        formData.append('autoDeductionBaseType', 'START_DATE');
      } else {
        formData.append('autoDeductionBaseType', 'FIXED_DAY');
        formData.append('autoDeductionBaseDay', values.autoDeductionBaseDay || 1);
      }
    }

    // 이미지 처리
    voucherImages.forEach((img, index) => {
      if (img.file) {
        // 새로 업로드하는 파일
        formData.append('images', img.file);
      } else if (img.url) {
        // 기존 이미지 URL (수정 모드)
        formData.append('imageStrings', img.url);
      }
    });

    // 커리큘럼 처리
    curList.forEach((cur, index) => {
      formData.append(`curriculums[${index}].title`, cur.title || '');
      formData.append(`curriculums[${index}].summary`, cur.outline || '');
      if (cur.images && cur.images.length > 0) {
        cur.images.forEach(img => {
          if (img.file) {
            formData.append(`curriculums[${index}].images`, img.file);
          } else if (img.url) {
            formData.append(`curriculums[${index}].imageStrings`, img.url);
          }
        });
      }
    });

    if (classDetail) formData.append('classDetail', classDetail);

    // 요일 설정 (allowedDays) - 중복 제거
    if (selectedDays.length > 0) {
      const uniqueDays = [...new Set(selectedDays)];
      formData.append('allowedDays', uniqueDays.join(','));
    }

    // 시간 설정 (LocalTime 형식: "HH:mm:ss")
    const startTimeStr = `${timeSettings.startHour}:${timeSettings.startMinute}:00`;
    const endTimeStr = `${timeSettings.endHour}:${timeSettings.endMinute}:00`;
    formData.append('startTime', startTimeStr);
    formData.append('endTime', endTimeStr);

    // 입장 가능 출입문 — 전환 지점은 항상 반영(빈 선택=전체 해제 플래그), 미전환은 기존대로.
    if (selectedDoorIds.length > 0) {
      selectedDoorIds.forEach(doorId => formData.append('accessGroupIds', doorId));
    } else if (doorBranchConverted) {
      formData.append('clearAccessGroups', 'true');
    }

    try {
      if (isEditMode) {
        await updateTicket(id, formData, user.token);
        toast.success(t('Successfully updated'));
        navigate('/ticket');
      } else {
        const result = await createTicket(formData, user.token);
        toast.success(t('Ticket created successfully'));
        // 그룹레슨이면 수업 선택 모달 띄우기
        const classType = getValues('courseClassType');
        if (classType === 'GROUP_LESSON' && result?.ticketId) {
          setNewTicketId(result.ticketId);
          try {
            const classes = await getAvailableClasses(result.ticketId, user.branch?.branchId, user.token);
            setAvailableClasses(classes || []);
            setClassSelectModalOpen(true);
          } catch (e) {
            navigate('/ticket');
          }
        } else {
          navigate('/ticket');
        }
      }
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  // trainer 선택 시 form value 업데이트
  useEffect(() => {
    if (ticketData.trainerIdsList.length > 0) {
      const optionIds = ticketData.trainerIdsList.map(opt => opt.id);
      setValue('trainerIds', optionIds);
    }
  }, [ticketData.trainerIdsList, setValue]);

  return (
    <>
    <PageContainer>
      {/* 생성 모드 단계 표시 UI */}
      {isCopyMode && (
        <CopyModeBanner>
          <ContentCopyOutlinedIcon sx={{ width: '18px', height: '18px', color: '#3E9FA7' }} />
          <CopyModeBannerTitle>복사본 만드는 중</CopyModeBannerTitle>
          <CopyModeBannerText>
            {copySourceName ? `'${copySourceName}'` : '원본'} 의 설정을 불러왔습니다. 저장해야 새 수강권으로 생성됩니다.
          </CopyModeBannerText>
        </CopyModeBanner>
      )}

      {!isEditMode && (
        <StepIndicatorContainer>
          <StepTabsWrapper>
            <StepTab active={step === 1} zIndex={4}>
              <StepTabShapeFirst active={step === 1} />
              <StepTabContent active={step === 1} isFirst>
                <StepTabNumber active={step === 1}>1</StepTabNumber>
                <StepTabLabel active={step === 1}>상품 정보</StepTabLabel>
              </StepTabContent>
            </StepTab>
            <StepTab active={step === 2} zIndex={3}>
              <StepTabShapeSecond active={step === 2} />
              <StepTabContent active={step === 2}>
                <StepTabNumber active={step === 2}>2</StepTabNumber>
                <StepTabLabel active={step === 2}>상품 설명</StepTabLabel>
              </StepTabContent>
            </StepTab>
          </StepTabsWrapper>
          <HeaderActionGroup>
            <LoadProductButton onClick={() => setPickerOpen(true)} variant="contained" disableElevation>
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
              기존 상품 불러오기
            </LoadProductButton>
            <BackToListLink as={Link} to="/ticket">
              <BackToListIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 19L8 12L15 5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </BackToListIcon>
              <BackToListText>목록으로</BackToListText>
            </BackToListLink>
          </HeaderActionGroup>
        </StepIndicatorContainer>
      )}

      <ProductPickerModal
        open={pickerOpen}
        kind="TICKET"
        onClose={() => setPickerOpen(false)}
        onSelect={selectedId => {
          setPickerOpen(false);
          navigate(`/ticket/create?copyFrom=${selectedId}`);
        }}
      />

      {/* Edit 모드 전용 헤더 */}
      {isEditMode && (
        <>
          <Divider />
          <EditModeHeader>
            <ToggleContainer>
              <ToggleLabel>판매 {watch('saleStatus') === 'APPROVED' ? 'ON' : 'OFF'}</ToggleLabel>
              <StyledSwitch
                checked={watch('saleStatus') === 'APPROVED'}
                onChange={handleChangeStatus}
                disabled={user.userRole === 'ROLE_MANAGER' && ticketData.saleStatus === 'APPROVAL_PENDING'}
              />
            </ToggleContainer>

            <TicketInfoWrapper>
              <TicketInfoHeader>
                <TicketInfoTitle>
                  {watch('ticketName')} {t(watch('classType'))} {watch('usageCount')}회{' '}
                  {`${watch('usagePeriod')}${usagePeriodType === 'MONTH' ? '개월' : '일'}`}
                </TicketInfoTitle>
                <StatusBadge variant="outlined">{sellStatus(watch('saleStatus'), t)}</StatusBadge>
                <FavoriteStar targetType={FAVORITE_TARGET.TICKET} targetId={Number(id)} favorite={favorite} onChanged={setFavorite} />
              </TicketInfoHeader>
              <WishlistWrapper>
                <WishlistIcon src="/images/heart.svg" alt="love" />
                <WishlistText>찜하기 수 {ticketData.wishListAddedCount ?? 0}</WishlistText>
              </WishlistWrapper>
            </TicketInfoWrapper>
            <CopyButton onClick={() => setChangeLogOpen(true)} size="large" sx={{ mr: '8px' }}>
              상품 변경 로그
              <HistoryOutlinedIcon sx={{ width: '16px', height: '16px' }} />
            </CopyButton>
            <CopyButton onClick={() => setCopyModalOpen(true)} size="large">
              수강권 복사
              <ContentCopyOutlinedIcon sx={{ width: '16px', height: '16px' }} />
            </CopyButton>
          </EditModeHeader>
          <Divider />

          <ProductChangeHistoryModal
            open={changeLogOpen}
            onClose={() => setChangeLogOpen(false)}
            productType="TICKET"
            productId={id}
            productName={watch('ticketName')}
            token={user.token}
          />

          {/* 복사 모달 */}
          <Modal open={copyModalOpen} onClose={() => setCopyModalOpen(false)}>
            <ModalContainer>
              <ModalCloseIcon as={GridCloseIcon} onClick={() => setCopyModalOpen(false)} />
              <ModalIconBadge>
                <ContentCopyOutlinedIcon sx={{ width: '22px', height: '22px', color: '#4AB3BC' }} />
              </ModalIconBadge>
              <ModalContent>
                <ModalTitle>이 수강권을 복사해 새 상품을 만들까요?</ModalTitle>
              </ModalContent>
              <ModalDescription>
                <ModalDescText>원본 설정이 채워진 수강권 등록 화면으로 이동합니다.</ModalDescText>
                <ModalDescTextSub>수강권명을 입력하고 저장해야 새 상품이 생성됩니다.</ModalDescTextSub>
              </ModalDescription>
              <ModalButtonWrapper>
                <ModalCancelButton onClick={() => setCopyModalOpen(false)} variant="contained">
                  {t('Cancel')}
                </ModalCancelButton>
                <ModalConfirmButton onClick={handleCopyTicket} variant="contained">
                  복사해서 만들기
                </ModalConfirmButton>
              </ModalButtonWrapper>
            </ModalContainer>
          </Modal>
        </>
      )}

      {step === 1 && (
        <TicketEditStep01
          control={control}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          user={user}
          trainers={trainers}
          data={data}
          additionalProductSettings={additionalProductSettings}
          handleAddProduct={handleAddProduct}
          handleRemoveProduct={handleRemoveProduct}
          selectedDays={selectedDays}
          handleDayToggle={handleDayToggle}
          timeSettings={timeSettings}
          handleTimeChange={handleTimeChange}
          usagePeriodType={usagePeriodType}
          setUsagePeriodType={setUsagePeriodType}
          isTaxFree={isTaxFree}
          setIsTaxFree={setIsTaxFree}
          discountType={discountType}
          setDiscountType={setDiscountType}
          hasDiscountEndDate={hasDiscountEndDate}
          setHasDiscountEndDate={setHasDiscountEndDate}
          handleRegularPriceChange={handleRegularPriceChange}
          handleDiscountRateChange={handleDiscountRateChange}
          handleFinalPriceChange={handleFinalPriceChange}
          handleTrainerOnChange={handleTrainerOnChange}
          onNext={handleNextStep}
          onCancel={handleCancel}
          loader={loader}
          isEditMode={isEditMode}
          copySourceName={copySourceName}
          DAYS={DAYS}
          HOURS={HOURS}
          MINUTES={MINUTES}
          CLASS_TYPES={CLASS_TYPES}
          centrerInfo={centrerInfo}
          exposureYn={exposureYn}
          onExposureToggle={handleExposureToggle}
          selectedDoorIds={selectedDoorIds}
          onDoorIdsChange={setSelectedDoorIds}
          onDoorConvertedChange={setDoorBranchConverted}
          branchOptions={directBranches || []}
          legacyBranchNames={legacyBranchNames}
        />
      )}

      {step === 2 && (
        <>
          <TicketEditStep02
            aiToggle={aiToggle}
            setAiToggle={setAiToggle}
            voucherImages={voucherImages}
            handleVoucherImageUpload={handleVoucherImageUpload}
            handleRemoveVoucherImage={handleRemoveVoucherImage}
            curList={curList}
            handleAddCurriculum={handleAddCurriculum}
            handleRemoveCurriculum={handleRemoveCurriculum}
            handleCurriculumChange={handleCurriculumChange}
            handleCurriculumImageUpload={handleCurriculumImageUpload}
            handleRemoveCurriculumImage={handleRemoveCurriculumImage}
            classDetail={classDetail}
            setClassDetail={setClassDetail}
            onPrev={handlePrevStep}
            onSubmit={handleSubmit(submit)}
            onCancel={handleCancel}
            loader={loader}
            isEditMode={isEditMode}
            onDelete={handleDeleteTicket}
            canSave={canSave}
            exposureYn={exposureYn}
            setExposureYn={setExposureYn}
          />
        </>
      )}
    </PageContainer>

    <ConfirmModal
      open={exposureAlertOpen}
      onClose={() => setExposureAlertOpen(false)}
      onConfirm={() => setExposureAlertOpen(false)}
      message={`판매종료된 상품은\n앱 노출이 불가합니다.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />

    {/* 수업 선택 모달 */}
    <ClassSelectModal
      open={classSelectModalOpen}
      onClose={() => { setClassSelectModalOpen(false); navigate('/ticket'); }}
      availableClasses={availableClasses}
      newTicketId={newTicketId}
      user={user}
      toast={toast}
      onDone={() => { setClassSelectModalOpen(false); navigate('/ticket'); }}
    />
    </>
  );
}

export default TicketEdit;
